import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_COMMITTEEMEMBER_LOAD_BY_ID,
    ACTION_COMMITTEEMEMBER_LOAD_BY_COMMITTEE_ID,
    ACTION_COMMITTEEMEMBER_LOAD_FAILURE,
    ACTION_COMMITTEEMEMBER_LOAD_SUCCESS,

    ACTION_COMMITTEEMEMBER_SAVE,
    ACTION_COMMITTEEMEMBER_SAVE_SUCCESS,
    ACTION_COMMITTEEMEMBER_SAVE_FAILURE,

    ACTION_COMMITTEEMEMBER_CREATE,
    ACTION_COMMITTEEMEMBER_CREATE_SUCCESS,
    ACTION_COMMITTEEMEMBER_CREATE_FAILURE,

    ACTION_COMMITTEEMEMBER_VALIDATE,
    ACTION_COMMITTEEMEMBER_VALIDATE_SUCCESS,
    ACTION_COMMITTEEMEMBER_VALIDATE_FAILURE,

    ACTION_COMMITTEEMEMBER_CLEAR,
    ACTION_COMMITTEEMEMBER_FILTER,

    CommitteeMemberAction
} from '../actions/committeeMember';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ICommitteeMemberState {
    formData: Dtos.CommitteeMember | Dtos.CommitteeMember[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.CommitteeMember[];
    formLength: number;
}

const initialState: ICommitteeMemberState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ICommitteeMemberState, _isLoading: boolean): ICommitteeMemberState {
    const newState: ICommitteeMemberState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ICommitteeMemberState,
    formData: Dtos.CommitteeMember | Dtos.CommitteeMember[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ICommitteeMemberState {
    const newState: ICommitteeMemberState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ICommitteeMemberState, responseStatus: Dtos.ResponseStatus): ICommitteeMemberState {
    const newState: ICommitteeMemberState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ICommitteeMemberState, formFilter: IFormFilter): ICommitteeMemberState {

    const items: Dtos.CommitteeMember[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            (p.personnel.displayWithSalutation && p.personnel.displayWithSalutation.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)
        )) : undefined;

    const newState: ICommitteeMemberState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ICommitteeMemberState, propertiesToClear?: string[]): ICommitteeMemberState {

    if (!propertiesToClear) {
        const newState: ICommitteeMemberState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ICommitteeMemberState, _isSaving: boolean): ICommitteeMemberState {
    const newState: ICommitteeMemberState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ICommitteeMemberState,
    formData: Dtos.CommitteeMember | Dtos.CommitteeMember[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ICommitteeMemberState {
    const newState: ICommitteeMemberState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ICommitteeMemberState, responseStatus: Dtos.ResponseStatus): ICommitteeMemberState {
    const newState: ICommitteeMemberState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ICommitteeMemberState, _isCreating: boolean): ICommitteeMemberState {
    const newState: ICommitteeMemberState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ICommitteeMemberState,
    formData: Dtos.CommitteeMember,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ICommitteeMemberState {
    const newState: ICommitteeMemberState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ICommitteeMemberState, responseStatus: Dtos.ResponseStatus): ICommitteeMemberState {
    const newState: ICommitteeMemberState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: ICommitteeMemberState, _isValidating: boolean): ICommitteeMemberState {
    const newState: ICommitteeMemberState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ICommitteeMemberState,
    formData: Dtos.CommitteeMember,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ICommitteeMemberState {
    const newState: ICommitteeMemberState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ICommitteeMemberState, responseStatus: Dtos.ResponseStatus): ICommitteeMemberState {
    const newState: ICommitteeMemberState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const CommitteeMemberReducer: Reducer<ICommitteeMemberState> = (state: ICommitteeMemberState = initialState, action: CommitteeMemberAction) => {
    switch (action.type) {
        case ACTION_COMMITTEEMEMBER_LOAD_BY_ID:
        case ACTION_COMMITTEEMEMBER_LOAD_BY_COMMITTEE_ID:
            return loading(state, true);
        case ACTION_COMMITTEEMEMBER_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_COMMITTEEMEMBER_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEMBER_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_COMMITTEEMEMBER_SAVE:
            return saving(state, true);
        case ACTION_COMMITTEEMEMBER_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_COMMITTEEMEMBER_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEMBER_CREATE:
            return creating(state, true);
        case ACTION_COMMITTEEMEMBER_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_COMMITTEEMEMBER_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEMBER_VALIDATE:
            return validating(state, true);
        case ACTION_COMMITTEEMEMBER_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_COMMITTEEMEMBER_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEMBER_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default CommitteeMemberReducer;